import { useState } from "react";
import type { ProtocolResponse } from "@/mocks/protocolResponses";
import ProtocolTimeline from "./ProtocolTimeline";
import ProtocolChecklist from "./ProtocolChecklist";
import ProtocolAlertBox from "./ProtocolAlertBox";

interface Props {
  data: ProtocolResponse;
}

export default function MobileProtocolView({ data }: Props) {
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  if (data.pasos.length === 0) return null;

  const nextStep = data.pasos[0];
  const criticalAlerts = data.alertas.filter((a) => a.tipo === "critica");

  return (
    <div className="lg:hidden space-y-4">
      {/* Next recommended step */}
      <div className="bg-white border-2 border-primary-200 rounded-xl overflow-hidden">
        <div className="bg-primary-500 text-white px-4 py-2.5 flex items-center gap-2">
          <i className="ri-arrow-right-circle-fill text-lg" />
          <span className="text-sm font-semibold">Próximo paso recomendado</span>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="w-7 h-7 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
              {nextStep.numero}
            </span>
            <h4 className="text-sm font-semibold text-foreground-900">{nextStep.titulo}</h4>
          </div>
          <p className="text-sm text-foreground-700 leading-relaxed mb-3">{nextStep.accion}</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 text-primary-700 bg-primary-50 px-2 py-1 rounded-lg">
              <i className="ri-user-line" />
              {nextStep.responsable}
            </span>
            <span className="inline-flex items-center gap-1 text-foreground-500 bg-background-100 px-2 py-1 rounded-lg">
              <i className="ri-time-line" />
              {nextStep.plazo}
            </span>
            <span className="inline-flex items-center gap-1 text-accent-700 bg-accent-50 px-2 py-1 rounded-lg">
              <i className="ri-file-list-3-line" />
              {nextStep.registro_requerido}
            </span>
          </div>
        </div>
      </div>

      {/* Critical alerts */}
      {criticalAlerts.length > 0 && (
        <div className="space-y-2">
          {criticalAlerts.map((alert, idx) => (
            <div key={idx} className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
              <i className="ri-error-warning-fill text-red-500 text-sm flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-800 leading-relaxed">{alert.mensaje}</p>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => setShowAllSteps(!showAllSteps)}
          className="flex items-center justify-center gap-1.5 text-sm font-medium text-foreground-700 bg-white border border-background-200 rounded-xl px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-list-check-2 text-base" />
          {showAllSteps ? "Ocultar pasos" : `Ver todos los pasos (${data.pasos.length})`}
        </button>
        <button
          onClick={() => setShowChecklist(!showChecklist)}
          className="flex items-center justify-center gap-1.5 text-sm font-medium text-foreground-700 bg-white border border-background-200 rounded-xl px-4 py-3 hover:bg-background-50 transition-colors cursor-pointer"
        >
          <i className="ri-check-double-line text-base" />
          {showChecklist ? "Ocultar checklist" : "Checklist rápido"}
        </button>
      </div>

      {/* Expanded: All steps */}
      {showAllSteps && (
        <div className="bg-white border border-background-200/80 rounded-xl p-4">
          <ProtocolTimeline steps={data.pasos} />
        </div>
      )}

      {/* Expanded: Checklist */}
      {showChecklist && (
        <div className="bg-white border border-background-200/80 rounded-xl p-4">
          <ProtocolChecklist items={data.checklist} />
        </div>
      )}

      {/* Other alerts always visible */}
      <ProtocolAlertBox alerts={data.alertas} />
    </div>
  );
}